import { BlogArticle, BlogArticleKey } from "@/app/types/BlogArticle";
import { ArticleBlockType } from "@/app/types/BlogArticleContent";
import { BlogSlugs, ProtectedPaths } from "@/app/vars/Routes";

export const salesNavigatorArticle: BlogArticle = {
  key: BlogArticleKey.SALES_NAVIGATOR,
  slug: BlogSlugs.SALES_NAVIGATOR,
  categoryId: "03",
  date: "2026-08-30",
  readTime: 7,
  locales: {
    fr: {
      title: "Sales Navigator : ce qu'il trouve, et ce qu'il ne trouvera jamais",
      excerpt:
        "Ses filtres sont excellents pour décrire une entreprise. Ils sont muets sur la seule chose qui compte : est-ce que cette personne a le problème que tu règles.",
      description:
        "Ce que Sales Navigator fait bien, la limite de ses filtres, et comment trier une liste sur autre chose qu'un intitulé de poste.",
      ctaTitle: "Trie ta liste sur tes critères, pas sur des filtres",
      ctaText:
        "On écrit tes critères à partir de tes appels clients, puis on lit les profils un par un et on te dit lesquels collent et pourquoi. Sans engagement.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La question revient à chaque fois qu'on parle de prospection : est-ce que Sales Navigator vaut son prix. La réponse dépend entièrement de ce que tu attends de lui, et la plupart des déceptions viennent d'une attente qu'il n'a jamais promis de remplir.",
        },
        { type: ArticleBlockType.H2, text: "Ce qu'il fait très bien" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Il n'y a pas de débat sur ce point. Pour délimiter un ensemble d'entreprises et de personnes selon des attributs déclarés, il n'existe pas d'équivalent : taille, secteur, ancienneté dans le poste, croissance des effectifs, zone géographique, changements récents.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Si ton problème est *je ne sais pas où chercher*, il le règle en une après-midi. Tu passes d'un marché flou à une liste de deux mille personnes réelles, ce qui est un progrès énorme.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "Un filtre décrit ce que quelqu'un est. Il ne dira jamais ce que cette personne est en train de vivre.",
        },
        { type: ArticleBlockType.H2, text: "La limite, et elle est structurelle" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Tous les filtres reposent sur des champs déclaratifs, remplis par la personne pour se présenter. Or ce qui décide qu'elle achète n'est presque jamais dans ces champs.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["Ce que le filtre sait", "Ce qu'il ne saura jamais"],
          rows: [
            [
              "Directeur commercial, cinquante personnes, logiciel",
              "S'il a perdu deux affaires ce trimestre faute de suivi",
            ],
            [
              "En poste depuis quatre mois",
              "S'il a hérité d'une équipe sans process ou d'une machine bien huilée",
            ],
            [
              "L'entreprise a recruté dix personnes",
              "Si elle recrute parce qu'elle grandit ou parce qu'elle remplace",
            ],
            [
              "Utilise tel outil, d'après son profil",
              "S'il en est satisfait, ou s'il cherche à en sortir",
            ],
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "La colonne de droite est celle qui fait signer. Aucun abonnement ne te la donnera, parce que cette information n'est pas déclarée : elle se déduit en lisant un profil entier, ou elle s'entend en appel.",
        },
        { type: ArticleBlockType.H2, text: "Ce que ça donne en pratique" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le scénario est presque toujours le même. Tu construis une recherche propre, tu obtiens deux mille profils, et tu te retrouves devant un problème que tu n'avais pas avant : lesquels contacter en premier.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Deux réponses possibles, et elles n'ont pas le même coût. Contacter tout le monde avec le même message, ce qui abîme le taux d'acceptation et le compte avec. Ou lire les profils un par un, ce qui prend trois minutes chacun, soit cent heures pour la liste.",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "Une liste filtrée, non triée",
            items: [
              "Deux mille profils qui se ressemblent",
              "Aucune raison de commencer par l'un plutôt que l'autre",
              "Un message qui doit marcher pour tous",
              "Un taux de réponse qui décide à ta place",
            ],
          },
          good: {
            title: "La même liste, triée",
            items: [
              "Quarante profils retenus sur un critère précis",
              "Un ordre justifié, du plus proche au plus lointain",
              "Un message par personne, appuyé sur ce qui l'a fait retenir",
              "Un taux de réponse qui rend la liste rentable",
            ],
          },
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "L'abonnement n'est donc pas en cause. Il produit exactement ce qu'il promet, une liste. C'est l'étape suivante qui manque, et personne ne la vend avec.",
        },
        { type: ArticleBlockType.H2, text: "Ce qui manque entre le filtre et le message" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Entre les deux, il y a un tri, et ce tri demande deux choses que Sales Navigator ne peut pas fournir.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Des critères écrits qui viennent de tes clients.** Pas des attributs d'entreprise, des phrases qui se tranchent en lisant un profil, et qui décrivent la situation dans laquelle les gens achètent.",
            "**Une lecture profil par profil.** Un filtre coche une case. Un tri lit un parcours, une description, un changement récent, et en tire une raison.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Cette raison a une deuxième vie que presque personne n'exploite : c'est ton premier message. Un profil retenu sans raison ne t'apprend rien à écrire ; un profil retenu parce qu'il a pris son poste il y a quatre mois dans une équipe qui vient de doubler te donne la première phrase.",
        },
        { type: ArticleBlockType.H2, text: "Faut-il le prendre ?" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Si tu ne sais pas encore délimiter ton marché, oui, et il se rentabilise vite. Si tu sais déjà où chercher et que ton problème est de choisir qui contacter dans une liste que tu as déjà, il ne réglera rien, et une deuxième licence encore moins.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Le test est simple : ouvre ta dernière recherche. Si tu peux dire, profil par profil, pourquoi celui-là et pas celui d'à côté, tu tires tout ce qu'il y a à en tirer. Sinon, ce n'est pas de plus de filtres que tu as besoin.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Là où Meidly aide",
          text: "On part de tes appels clients pour écrire les critères de ceux qui achètent vraiment, puis l'extension lit les profils que tu as sous les yeux et te dit lesquels collent, avec la raison de chacun. Ça ne remplace pas une recherche LinkedIn, ça reprend là où elle s'arrête.",
          linkLabel: "Découvrir la recherche de prospects",
          linkPath: ProtectedPaths.PROSPECT_SEARCH,
        },
        { type: ArticleBlockType.H2, text: "En résumé" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Sales Navigator délimite un marché, et il le fait mieux que tout le reste. Il ne trie pas, parce que ce qui sert à trier n'est écrit dans aucun champ. La liste qu'il produit ne vaut donc que ce que vaut l'étape d'après, et c'est cette étape qu'il faut outiller.",
        },
      ],
      faq: [
        {
          question: "Sales Navigator vaut-il son prix ?",
          answer:
            "Oui si ton problème est de délimiter un marché : il n'a pas d'équivalent pour ça. Non si ton problème est de choisir qui contacter dans une liste que tu as déjà, parce que ses filtres reposent sur des champs déclaratifs qui ne disent pas si la personne a ton problème.",
        },
        {
          question: "Pourquoi une liste Sales Navigator ne convertit-elle pas ?",
          answer:
            "Parce qu'une liste filtrée n'est pas une liste triée. Deux mille profils correspondant aux mêmes attributs ne donnent aucune raison de commencer par l'un plutôt que l'autre, donc le même message part à tout le monde, et c'est lui qui ne convertit pas.",
        },
        {
          question: "Quels critères utiliser pour trier une liste de prospects ?",
          answer:
            "Des critères tirés de tes clients actuels et qui se tranchent en lisant un profil : une situation, un changement récent, une conséquence visible. Pas des attributs d'entreprise, que le filtre a déjà appliqués et qui ne distinguent plus rien à ce stade.",
        },
        {
          question: "Faut-il lire chaque profil avant de contacter ?",
          answer:
            "Oui, et c'est justement ce qui coûte cher : trois minutes par profil font cent heures pour deux mille. C'est cette lecture qu'il faut outiller, pas la recherche, parce qu'elle produit aussi la raison qui servira de première phrase à ton message.",
        },
      ],
    },
    en: {
      title: "Sales Navigator: what it finds, and what it never will",
      excerpt:
        "Its filters are excellent at describing a company. They are silent on the only thing that matters: whether this person has the problem you solve.",
      description:
        "What Sales Navigator does well, the limit of its filters, and how to sort a list on something other than a job title.",
      ctaTitle: "Sort your list on your criteria, not on filters",
      ctaText:
        "We write your criteria from your customer calls, then read profiles one by one and tell you which ones fit and why. No strings attached.",
      body: [
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The question comes up every time prospecting does: is Sales Navigator worth its price. The answer depends entirely on what you expect from it, and most disappointment comes from an expectation it never promised to meet.",
        },
        { type: ArticleBlockType.H2, text: "What it does very well" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "There is no argument here. For carving out a set of companies and people by declared attributes, nothing else compares: size, industry, tenure, headcount growth, geography, recent changes.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "If your problem is *I do not know where to look*, it solves that in an afternoon. You go from a vague market to a list of two thousand real people, which is an enormous step.",
        },
        {
          type: ArticleBlockType.LEAD,
          text: "A filter describes what someone is. It will never tell you what that person is going through.",
        },
        { type: ArticleBlockType.H2, text: "The limit, and it is structural" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Every filter rests on declared fields, filled in by the person to present themselves. What decides that they buy is almost never in those fields.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["What the filter knows", "What it will never know"],
          rows: [
            [
              "Sales director, fifty people, software",
              "Whether he lost two deals this quarter to poor follow-up",
            ],
            [
              "In role for four months",
              "Whether she inherited a team with no process or a well-oiled machine",
            ],
            ["The company hired ten people", "Whether it is hiring to grow or to replace"],
            [
              "Uses a given tool, per the profile",
              "Whether they are happy with it, or trying to get out",
            ],
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The right-hand column is what closes deals. No subscription hands it to you, because that information is not declared: it is inferred by reading a whole profile, or heard on a call.",
        },
        { type: ArticleBlockType.H2, text: "What it looks like in practice" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The scenario is almost always the same. You build a clean search, you get two thousand profiles, and you face a problem you did not have before: which ones to contact first.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Two possible answers, at very different costs. Contact everyone with the same message, which wrecks your acceptance rate and your account with it. Or read profiles one by one, three minutes each, a hundred hours for the list.",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "A filtered list, not sorted",
            items: [
              "Two thousand profiles that look alike",
              "No reason to start with one rather than another",
              "A message that has to work for everyone",
              "A reply rate that decides for you",
            ],
          },
          good: {
            title: "The same list, sorted",
            items: [
              "Forty profiles kept on a precise criterion",
              "A justified order, from closest to furthest",
              "One message per person, built on what kept them",
              "A reply rate that makes the list pay",
            ],
          },
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "So the subscription is not at fault. It delivers exactly what it promises, a list. It is the next step that is missing, and nobody sells it alongside.",
        },
        { type: ArticleBlockType.H2, text: "What sits between the filter and the message" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Between the two there is a sort, and that sort needs two things Sales Navigator cannot supply.",
        },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Written criteria that come from your customers.** Not company attributes, but sentences you can settle while reading a profile, describing the situation people are in when they buy.",
            "**A profile-by-profile read.** A filter ticks a box. A sort reads a career, a description, a recent change, and draws a reason from it.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "That reason has a second life almost nobody uses: it is your first message. A profile kept for no stated reason teaches you nothing to write; a profile kept because they started four months ago in a team that just doubled hands you the opening line.",
        },
        { type: ArticleBlockType.H2, text: "Should you buy it?" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "If you cannot yet carve out your market, yes, and it pays for itself quickly. If you already know where to look and your problem is choosing whom to contact in a list you already have, it will fix nothing, and a second licence even less.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The test is simple: open your last search. If you can say, profile by profile, why this one and not the one beside it, you are getting everything there is. If not, more filters are not what you need.",
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "Where Meidly helps",
          text: "We start from your customer calls to write the criteria of the people who actually buy, then the extension reads the profiles in front of you and tells you which ones fit, with a reason for each. It does not replace a LinkedIn search, it picks up where that search stops.",
          linkLabel: "See how prospect search works",
          linkPath: ProtectedPaths.PROSPECT_SEARCH,
        },
        { type: ArticleBlockType.H2, text: "In short" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Sales Navigator carves out a market, and does it better than anything else. It does not sort, because what you sort on is written in no field. The list it produces is therefore worth exactly what the next step is worth, and that is the step to equip.",
        },
      ],
      faq: [
        {
          question: "Is Sales Navigator worth the price?",
          answer:
            "Yes if your problem is carving out a market: nothing else compares. No if your problem is choosing whom to contact in a list you already have, because its filters rest on declared fields that do not say whether the person has your problem.",
        },
        {
          question: "Why does a Sales Navigator list not convert?",
          answer:
            "Because a filtered list is not a sorted list. Two thousand profiles matching the same attributes give no reason to start with one rather than another, so the same message goes to everyone, and that is what fails to convert.",
        },
        {
          question: "Which criteria should you use to sort a prospect list?",
          answer:
            "Criteria drawn from your current customers that can be settled while reading a profile: a situation, a recent change, a visible consequence. Not company attributes, which the filter already applied and which no longer distinguish anything at this stage.",
        },
        {
          question: "Do you have to read every profile before reaching out?",
          answer:
            "Yes, and that is exactly what is expensive: three minutes a profile is a hundred hours for two thousand. That read is what needs equipping, not the search, because it also produces the reason that becomes the opening line of your message.",
        },
      ],
    },
  },
};
